import {
  Autocomplete,
  Avatar,
  AvatarGroup,
  Box,
  Card,
  CircularProgress,
  createFilterOptions,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Modal,
  Pagination,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useMaterialUIController } from "context";
import { Form, Formik } from "formik";
import React, { useState, useEffect, useCallback, useRef } from "react";
import Swal from "sweetalert2";
import { enqueueSnackbar } from "notistack";
import { useSelector, useDispatch } from "react-redux";
import momentTz from "moment-timezone";
import { teamsAction } from "redux/slice/teams";
import * as Yup from "yup";
import Loader from "utils/Loader";
import { checkPermission } from "utils/HelperFunctions";
import { PERMISSIONS_NAME, GET_ALL_LOCATIONS, UNASSIGNED } from "lib/constants";
import {
  DriveFileRenameOutline,
  VisibilityOutlined,
  DeleteOutlineOutlined,
} from "@mui/icons-material";
import { getAPICall } from "lib/callApi";
import { useNavigate } from "react-router-dom";
import TooltipLight from "utils/TooltipLight";
import sampleProfileImage from "assets/images/sample.png";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { createTeam, deleteTeam, editTeam } from "../../../apis/team";
import AddTeamMemberModal from "./AddTeamMemberModal";

const validationSchema = Yup.object({
  name: Yup.string()
    .max(100, "Name cannot be more than 100 characters")
    .required("Name is required"),
  location: Yup.object().required("Location is required").typeError("Location is required"),
});

const filter = createFilterOptions();

function TeamTile({
  team,
  menuShowingDropdown,
  setMenuShowingDropdown,
  setSelectedTeamId,
  handleEditTeamModal,
  hasEditTeamPermission,
  fetchTeams,
}) {
  const theme = useTheme();
  const buttonRef = useRef(null);
  const unAssignedTeam = !team.id;
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const showSubMenu = useCallback(() => {
    setMenuShowingDropdown(team.id);
  }, [team.id, setMenuShowingDropdown]);

  const closeSubMenu = useCallback(() => {
    setMenuShowingDropdown(null);
    setIsHovered(false);
  }, [setMenuShowingDropdown]);

  const handleDeleteTeam = async (data) => {
    if (!data.id) return;
    Swal.fire({
      title: "Are you sure?",
      text: `Are you sure you want to delete this team?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: theme.palette.primaryRed.main,
      cancelButtonColor: theme.palette.gray[80],
      confirmButtonText: "Yes, Delete it!",
    })
      .then(async (result) => {
        if (result.isConfirmed) {
          try {
            const deleteTeamResponse = await deleteTeam(data?.id);
            if (deleteTeamResponse?.data?.success) {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "success",
                  title: "Deleted",
                  text: "Team successfully deleted.",
                }),
                {
                  variant: "custom",
                }
              );
              fetchTeams();
            } else {
              enqueueSnackbar(
                JSON.stringify({
                  icon: "error",
                  title: "Error",
                  text: deleteTeamResponse?.data?.message || "Something Went Wrong",
                }),
                {
                  variant: "custom",
                }
              );
              fetchTeams();
            }
          } catch (error) {
            enqueueSnackbar(
              JSON.stringify({
                icon: "error",
                title: "Error",
                text: error.response.data.message || error.message || "Something went wrong",
              }),
              {
                variant: "custom",
              }
            );
          }
        }
      })
      .catch();
  };

  const renderTooltip = () => (
    <List sx={{ maxHeight: "450px", overflow: "auto" }}>
      {team?.UserCompaniesLinks?.length
        ? team.UserCompaniesLinks.map((user, index) => (
            <React.Fragment key={`user-tooltip-${user.id}`}>
              <ListItem>
                <ListItemAvatar
                  sx={{ "& .MuiAvatar-root": { width: 28, height: 28, fontSize: 15 } }}
                >
                  <Avatar
                    variant="circular"
                    alt={user.name.toUpperCase()}
                    src={user?.profilePicture || sampleProfileImage}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography component="h5" variant="body2" color="#888">
                      {user.name}
                    </Typography>
                  }
                />
              </ListItem>
              {index < team.UserCompaniesLinks.length - 1 && (
                <Divider component="li" sx={{ my: "0.4rem" }} />
              )}
            </React.Fragment>
          ))
        : null}
    </List>
  );

  return (
    <Grid item xs={12} sm={6} lg={4} md={6} key={team.id}>
      <Card
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          padding: "20px",
          cursor: "pointer",
        }}
        onClick={() => {
          if (!menuShowingDropdown) setSelectedTeamId(team.id || UNASSIGNED);
        }}
      >
        <Stack spacing={1} p={1} sx={{ height: "100%" }}>
          <MDBox sx={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
            <Typography variant="h5" component="h5">
              {team.name}
            </Typography>
            <MDBox
              sx={{ display: "flex", justifyContent: "end", alignItems: "end" }}
              ref={buttonRef}
            >
              {isHovered && hasEditTeamPermission && !unAssignedTeam ? (
                <>
                  <MoreHorizIcon
                    id={`team-tile-menu-icon-${team.id}`}
                    fontSize="medium"
                    sx={{
                      p: 0,
                      ":hover": {
                        cursor: "pointer",
                      },
                    }}
                    onClick={(e) => {
                      e?.stopPropagation();
                      showSubMenu();
                    }}
                  />
                  <Menu
                    // PaperProps={{ // required when need to open menu on hover instead on click
                    //   onMouseEnter: () => {
                    //     showSubMenu();
                    //   },
                    //   onMouseLeave: () => {
                    //     closeSubMenu();
                    //   },
                    // }}
                    anchorEl={buttonRef.current}
                    open={menuShowingDropdown === team.id}
                    onClose={closeSubMenu}
                  >
                    <MenuItem
                      onClick={() => {
                        setSelectedTeamId(team.id);
                      }}
                      key={`view-${team.id}`}
                    >
                      <MDBox sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton
                          size="small"
                          disableRipple
                          color="inherit"
                          aria-controls="team-tile-menu"
                          aria-haspopup="true"
                          variant="contained"
                          id={`team-tile-menu-edit-icon-${team.id}`}
                        >
                          <VisibilityOutlined
                            fontSize="medium"
                            id={`team-tile-menu-edit-icon-${team.id}`}
                          />
                        </IconButton>
                        View
                      </MDBox>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setMenuShowingDropdown(null);
                        handleEditTeamModal(team);
                      }}
                      key={`edit-team-name-${team.id}`}
                    >
                      <MDBox sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton
                          size="small"
                          disableRipple
                          color="inherit"
                          aria-controls="team-tile-menu"
                          aria-haspopup="true"
                          variant="contained"
                          id={`team-tile-menu-edit-icon-${team.id}`}
                        >
                          <DriveFileRenameOutline
                            fontSize="medium"
                            id={`team-tile-menu-edit-icon-${team.id}`}
                          />
                        </IconButton>
                        Edit Team Name
                      </MDBox>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        setMenuShowingDropdown(null);
                        handleDeleteTeam(team);
                      }}
                      key={`delete-team-name-${team.id}`}
                    >
                      <MDBox sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton
                          size="small"
                          disableRipple
                          color="inherit"
                          aria-controls="team-tile-menu"
                          aria-haspopup="true"
                          variant="contained"
                          id={`team-tile-menu-delete-icon-${team.id}`}
                        >
                          <DeleteOutlineOutlined
                            fontSize="medium"
                            id={`team-tile-menu-delete-icon-${team.id}`}
                          />
                        </IconButton>
                        Delete Team
                      </MDBox>
                    </MenuItem>
                  </Menu>
                </>
              ) : null}
            </MDBox>
          </MDBox>
          {unAssignedTeam ? null : (
            <Typography variant="body2">Location, {team?.TeamLocation?.location || "-"}</Typography>
          )}
          <Stack
            direction="row"
            sx={{
              alignItems: "flex-end",
              height: "100%",
              minHeight: "30px",
              justifyContent: "space-between",
              mt: "auto",
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", color: "#818da2" }}>
              {team?.UserCompaniesLinks?.length || 0} User
              {team?.UserCompaniesLinks?.length > 1 ? "s" : ""}
            </Typography>
            {team?.UserCompaniesLinks?.length ? (
              <TooltipLight title={renderTooltip()} sx={{ minWidth: "150px" }} placement="right">
                <AvatarGroup
                  max={4}
                  sx={{
                    "& .MuiAvatar-root": { width: 28, height: 28, fontSize: 15 },
                    "& .MuiAvatar-colorDefault": {
                      backgroundColor: "#fff",
                      color: "#818da2",
                      borderColor: "#fff",
                      fontWeight: "600",
                      pl: "0.25rem",
                    },
                  }}
                >
                  {team.UserCompaniesLinks.map((user) => (
                    <Avatar
                      key={`avatar-${user.id}`}
                      alt={user.name.toUpperCase()}
                      src={user.profilePicture || sampleProfileImage}
                    >
                      {user.name.slice(0, 1)}
                    </Avatar>
                  ))}
                </AvatarGroup>
              </TooltipLight>
            ) : null}
          </Stack>
        </Stack>
      </Card>
    </Grid>
  );
}

function TeamsList({ setSelectedTeamId, handleSeatCountCheck }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const teams = useSelector((state) => state.teams.teams);
  const totalTeams = useSelector((state) => state.teams.totalTeams);
  const loading = useSelector((state) => state.teams.loading);
  const user = useSelector((state) => state.loginUser.loginUserState);
  const hasEditTeamPermission = checkPermission(PERMISSIONS_NAME.MANAGE_USERS, user.userPermission);

  const [open, setOpen] = useState(false);
  const [isCreateMemberModalOpen, setIsCreateMemberModalOpen] = useState(false);
  const [teamsPayload, setTeamsPayload] = useState({
    pageSize: 9,
    pageNumber: 0,
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleOpen = () => setOpen(true);

  const [locations, setLocations] = useState([]);
  const [locationsFetchLoading, setLocationsFetchLoading] = useState(false);

  const [menuShowingDropdown, setMenuShowingDropdown] = useState(null);
  const [editTeamData, setEditTeamData] = useState(null);

  const handleEditTeamModal = (data) => {
    setEditTeamData(data);
    setOpen(true);
  };

  const handleMenuShowingDropdownChange = useCallback((menuTitle) => {
    setMenuShowingDropdown(menuTitle);
  }, []);

  const handleClose = () => {
    if (!isLoading) {
      setEditTeamData(null);
      setOpen(false);
    }
  };

  const [controller] = useMaterialUIController();

  const { darkMode } = controller;

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: window.innerWidth < 400 ? "100%" : 400,
    bgcolor: darkMode ? "#1F283E" : "background.paper",
    boxShadow: 24,
    p: 4,
    outline: "none",
    borderRadius: "0.5rem",
  };

  const fetchLocations = async () => {
    try {
      setLocationsFetchLoading(true);
      const response = await getAPICall(GET_ALL_LOCATIONS, true);
      if (response?.data?.success) {
        setLocations(response.data.data);
      } else {
        handleClose();
        if (response.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: response.response.data.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
      setLocationsFetchLoading(false);
    } catch (error) {
      setLocationsFetchLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const fetchTeams = () => {
    dispatch(teamsAction.getTeamsSaga({ ...teamsPayload, includeUsers: true }));
  };

  useEffect(() => {
    if (checkPermission(PERMISSIONS_NAME.MANAGE_USERS, user.userPermission) && open) {
      fetchLocations();
    }
  }, [open]);

  useEffect(() => {
    fetchTeams();
  }, [teamsPayload]);

  const handleTeamCreate = async (data) => {
    try {
      setIsLoading(true);
      let location = {};
      if (data.location.id) {
        location = { ...data.location };
      } else {
        location = { name: data.location.location };
      }
      const newTeam = await createTeam({
        ...data,
        location,
        timezone: momentTz.tz.guess(),
      });
      if (newTeam?.data?.success) {
        handleClose();
        setIsLoading(false);
        setTeamsPayload({ pageNumber: 0, pageSize: 9 });

        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: "Team is added successfully!",
          }),
          {
            variant: "custom",
          }
        );
      } else {
        if (newTeam?.response?.status === 409) {
          setIsLoading(false);
          return;
        }
        handleClose();
        setIsLoading(false);
        if (newTeam?.response?.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: newTeam?.response?.status === 422 ? "info" : "error",
              title: "Error",
              text: newTeam?.response?.data?.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
          handleClose();
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
      handleClose();
    }
  };

  const handleEditCreate = async (data) => {
    try {
      if (!hasEditTeamPermission) {
        handleClose();
        return;
      }
      setIsLoading(true);
      let location = {};
      if (data.location.id) {
        location = { ...data.location };
      } else {
        location = { name: data.location.location };
      }
      const newTeam = await editTeam(editTeamData?.id, {
        ...data,
        location,
      });
      if (newTeam?.data?.success) {
        handleClose();
        setIsLoading(false);
        setTeamsPayload({ pageNumber: 0, pageSize: 9 });

        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: "Team is edited successfully!",
          }),
          {
            variant: "custom",
          }
        );
      } else {
        if (newTeam?.response?.status === 409) {
          setIsLoading(false);
          return;
        }
        handleClose();
        setIsLoading(false);
        if (newTeam?.response?.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: newTeam?.response?.status === 422 ? "info" : "error",
              title: "Error",
              text: newTeam?.response?.data?.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
          handleClose();
        }
      }
    } catch (error) {
      setIsLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
      handleClose();
    }
  };

  return (
    <MDBox sx={{ position: "relative" }}>
      <Loader loading={loading} />
      <MDBox>
        {/* Search box */}
        <Box
          sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          mt={3}
          mb={3}
        >
          <MDBox
            pr={1}
            sx={{
              "& .MuiInputLabel-formControl": { lineHeight: "15px" },
            }}
          >
            <MDInput
              label="Search"
              onChange={(event) =>
                setTeamsPayload((prev) => ({
                  ...prev,
                  searchBy: event.target.value,
                  pageNumber: 0,
                }))
              }
            />
          </MDBox>
          <MDBox pr={1} sx={{ display: "flex", gap: "1rem" }}>
            {checkPermission(PERMISSIONS_NAME.MANAGE_USERS, user.userPermission) && (
              <MDButton
                color="primaryRed"
                onClick={() => {
                  handleSeatCountCheck(() => setIsCreateMemberModalOpen(true));
                }}
              >
                + Add new user
              </MDButton>
            )}
            {checkPermission(PERMISSIONS_NAME.MANAGE_USERS, user.userPermission) && (
              <MDButton color="primaryRed" onClick={handleOpen}>
                + Add new team
              </MDButton>
            )}
            {checkPermission(PERMISSIONS_NAME.MANAGE_USERS, user.userPermission) && (
              <MDButton color="primaryRed" onClick={() => navigate("/settings/users/bulk-upload")}>
                + Bulk Upload
              </MDButton>
            )}
          </MDBox>
        </Box>

        {/* Card block inside grid */}
        <Grid
          container
          mt={2}
          spacing={4}
          direction="row"
          justifyContent="flex-start"
          alignItems="stretch"
        >
          {teams?.map((team) => (
            <TeamTile
              key={team.id}
              team={team}
              menuShowingDropdown={menuShowingDropdown}
              setMenuShowingDropdown={handleMenuShowingDropdownChange}
              setSelectedTeamId={setSelectedTeamId}
              handleEditTeamModal={handleEditTeamModal}
              hasEditTeamPermission={hasEditTeamPermission}
              fetchTeams={fetchTeams}
            />
          ))}
        </Grid>
        {totalTeams > teamsPayload.pageSize && (
          <MDBox
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 5,
            }}
          >
            <Pagination
              page={teamsPayload.pageNumber + 1}
              count={Math.ceil(totalTeams / teamsPayload.pageSize)}
              onChange={(_, page) => setTeamsPayload((prev) => ({ ...prev, pageNumber: page - 1 }))}
            />
          </MDBox>
        )}
      </MDBox>

      <AddTeamMemberModal
        setOpen={setIsCreateMemberModalOpen}
        open={isCreateMemberModalOpen}
        initialState={{ isAddNew: true }}
      />

      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <MDBox sx={style}>
          <Typography variant="h4" mb={2}>
            {editTeamData?.name ? "Edit Team" : "Create a new Team"}
          </Typography>
          <Formik
            enableReinitialize
            validateOnChange
            validationSchema={validationSchema}
            initialValues={{
              name: editTeamData?.name ? editTeamData?.name : "",
              location: editTeamData?.locationId
                ? {
                    id: editTeamData?.locationId,
                    location: editTeamData?.TeamLocation?.location,
                  }
                : null,
            }}
            onSubmit={editTeamData?.name ? handleEditCreate : handleTeamCreate}
          >
            {({ values, setFieldValue, touched, errors }) => (
              <Form
                onKeyDown={(keyEvent) => {
                  if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
                    keyEvent.preventDefault();
                  }
                }}
              >
                <Stack
                  spacing={2}
                  sx={{
                    "& .MuiFormHelperText-root": { color: "#d32f2f" },
                    "& .MuiInputLabel-formControl": { lineHeight: "20px !important" },
                  }}
                >
                  <MDInput
                    name="name"
                    label="Name"
                    value={values?.name}
                    onChange={(event) => {
                      setFieldValue("name", event.target.value);
                    }}
                    helperText={touched.name && errors.name}
                    error={touched.name && Boolean(errors.name)}
                    fullWidth
                  />
                  <Autocomplete
                    fullWidth
                    value={values.location}
                    onChange={(event, newValue) => {
                      if (!newValue) {
                        setFieldValue("location", null);
                      }
                      if (typeof newValue === "string") {
                        setFieldValue("location", {
                          location: newValue,
                        });
                      } else if (newValue && newValue.inputValue) {
                        // Create a new value from the user input
                        setFieldValue("location", {
                          location: newValue.inputValue,
                        });
                      } else {
                        setFieldValue("location", newValue);
                      }
                    }}
                    filterOptions={(options, params) => {
                      const filtered = filter(options, params);

                      const { inputValue } = params;
                      // Suggest the creation of a new value
                      const isExisting = options.some((option) => inputValue === option.location);
                      if (inputValue !== "" && !isExisting) {
                        filtered.push({
                          inputValue,
                          location: `Add "${inputValue}" Location`,
                        });
                      }

                      return filtered;
                    }}
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    id="free-solo-with-text-demo"
                    options={locations}
                    getOptionLabel={(option) => {
                      // Value selected with enter, right from the input
                      if (typeof option === "string") {
                        return option;
                      }
                      // Add "xxx" option created dynamically
                      if (option.inputValue) {
                        return option.inputValue;
                      }
                      // Regular option
                      return option.location || "";
                    }}
                    // eslint-disable-next-line react/jsx-props-no-spreading
                    renderOption={(props, option) => <li {...props}>{option.location || ""}</li>}
                    freeSolo
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Location"
                        helperText={touched.location && errors.location}
                        error={touched.location && Boolean(errors.location)}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {locationsFetchLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Stack>
                <MDBox sx={{ display: "flex", justifyContent: "end" }}>
                  <MDButton
                    color="gray80"
                    size="small"
                    onClick={handleClose}
                    sx={{ mt: 2, mr: 1, position: "relative" }}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    color="primaryRed"
                    size="small"
                    type="submit"
                    sx={{ mt: 2, position: "relative" }}
                    disabled={isLoading}
                  >
                    {!isLoading ? `${editTeamData?.name ? "Edit" : "Create"} Team` : "Loading..."}
                  </MDButton>
                </MDBox>
              </Form>
            )}
          </Formik>
        </MDBox>
      </Modal>
    </MDBox>
  );
}

export default TeamsList;
